/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l18n from '../../shared/strings';
import rollbackCreatedPreset from './rollbackCreatedPreset';
import { getPresets } from './getPresets';
import { withoutExpiresAt } from '../helpers/websiteSettings';

const IMAGE_FIELDS = ['logoUrl', 'avatarUrl', 'backgroundLoginPageUrl', 'thumbnailUrl'];
const IMAGE_PICKED = 'imagePicked';

const hasImageUrl = (url) => Boolean(url && url !== IMAGE_PICKED);

const getPresetImageUploaders = (sdk) => ({
  avatarUrl: sdk.websiteSettingsPresets.uploadAvatar,
  backgroundLoginPageUrl: sdk.websiteSettingsPresets.uploadBackground,
  thumbnailUrl: sdk.websiteSettingsPresets.uploadThumbnail,
  logoUrl: sdk.websiteSettingsPresets.uploadLogo,
});

const applySettledImageUploads = ({
  uploadResults,
  fieldsToUpload,
  settings,
  Logger,
  presetId,
}) => {
  const failedFields = [];
  let firstError;

  uploadResults.forEach((result, index) => {
    const field = fieldsToUpload[index];

    if (result.status === 'fulfilled') {
      settings[result.value.field] = result.value.url;
      return;
    }

    failedFields.push(field);
    if (!firstError) {
      firstError = result.reason;
    }

    Logger.error(new Error('Error uploading website preset image'), { error: result.reason }, [
      'UploadWebsitePresetImageFailed',
      {
        errorMessage: (result.reason && result.reason.message) || 'NoMessage',
        presetId,
        field,
      },
    ]);
  });

  return { failedFields, firstError };
};

export const createPreset = createAsyncThunk(
  'websiteSettings/createPreset',
  async ({
    name, settings, imageData, isPublished, updateWebsiteSettings,
  }, {
    dispatch,
    extra: {
      UiBlocker, sdk, utils, Logger, showErrorDialog,
    },
  }) => {
    let createdPresetId = null;

    try {
      UiBlocker.block('Saving preset...');

      const settingsForCreate = withoutExpiresAt({ ...settings });
      IMAGE_FIELDS.forEach((field) => {
        if (settingsForCreate[field]) settingsForCreate[field] = '';
      });

      const { data } = await sdk.websiteSettingsPresets.create(name, settingsForCreate);
      createdPresetId = data._id;

      const uploaders = getPresetImageUploaders(sdk);

      if (isPublished) {
        const fieldsToCopy = IMAGE_FIELDS.filter((field) => hasImageUrl(settings?.[field]));
        const uploadResults = await Promise.allSettled(
          fieldsToCopy.map(async (field) => {
            const { data: { url } } = await uploaders[field](data._id, settings[field]);
            return { field, url };
          }),
        );
        const { failedFields, firstError } = applySettledImageUploads({
          uploadResults,
          fieldsToUpload: fieldsToCopy,
          settings: data.settings,
          Logger,
          presetId: data._id,
        });

        if (failedFields.length) {
          throw firstError;
        }
      } else if (imageData) {
        const fieldsToUpload = IMAGE_FIELDS.filter((field) => (
          uploaders[field] && imageData[field]?.file
        ));
        const uploadResults = await Promise.allSettled(
          fieldsToUpload.map(async (field) => {
            const { data: { url } } = await uploaders[field](data._id, imageData[field].file).promise;
            return { field, url };
          }),
        );
        const { failedFields, firstError } = applySettledImageUploads({
          uploadResults,
          fieldsToUpload,
          settings: data.settings,
          Logger,
          presetId: data._id,
        });

        if (failedFields.length) {
          throw firstError;
        }

        updateWebsiteSettings(data.settings);
      }

      Logger.log('User', 'WebsitePresetCreated', { preset_name: data.name, preset_id: data._id });

      return data;
    } catch (error) {
      let rollbackFailed = false;

      if (createdPresetId) {
        const rollbackSucceeded = await rollbackCreatedPreset({
          presetId: createdPresetId,
          sdk,
          Logger,
        });

        if (!rollbackSucceeded) {
          rollbackFailed = true;

          try {
            await dispatch(getPresets());
          } catch (refreshError) {
            Logger.error(new Error('Error refreshing website presets after rollback failure'), { error: refreshError }, [
              'RefreshWebsitePresetsAfterRollbackFailed',
              { errorMessage: (refreshError && refreshError.message) || 'NoMessage', presetId: createdPresetId },
            ]);
          }
        }
      }

      Logger.log('User', 'WebsitePresetNotCreated');

      const uploadErrorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not be create preset';
      const errorMessage = rollbackFailed
        ? `${uploadErrorMessage}. ${l18n.WEBSITES.textPresetRollbackFailed}`
        : uploadErrorMessage;

      showErrorDialog(errorMessage);
      Logger.error(new Error('Error preset creating'), { error }, [
        'CreateWebsitePresetFailed',
        {
          errorMessage: (error && error.message) || 'NoMessage',
          rollbackFailed,
          presetId: createdPresetId || '',
        },
      ]);
      throw error;
    } finally {
      UiBlocker.unblock();
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      createPreset.fulfilled,
      (state, { payload }) => {
        state.presets.unshift(payload);
        state.error = null;
      },
    )
    .addCase(
      createPreset.rejected,
      (state, { error }) => {
        state.error = error;
      },
    );
};
